import HCaptcha from '@hcaptcha/react-hcaptcha'
import {Box, Flex} from '@indoqa/style-system'
import {IStyle} from 'fela'
import * as React from 'react'
import {useFela} from 'react-fela'
import {Theme} from '../../app/theme'
import {Button, Type} from '../../commons/components/button/Button'
import {ReaderFooterPanel} from '../../commons/components/footer/ReaderFooterPanel'
import {DigitalHelmet} from '../../commons/components/helmet/DigitalHelmet'
import {MainContainerPanel} from '../../commons/components/main/MainContainerPanel'
import {ReaderLayout} from '../../commons/layouts/reader-layout/ReaderLayout'
import {digitalLoginPage, digitalQueryParams} from '../../digital/store/digital.paths'
import {ARHeaderPanel} from '../components/header/ARHeaderPanel'
import {ArLink} from '../components/links/ArLink'
import {ArTrialAccountIntro} from '../components/trial-account/ArTrialAccountIntro'
import {DEFAULT_LANDINGPAGE, isLandingpage, Landingpage, supportsCampaign} from '../store/ar.landingpage'
import {arPathTrialAccount} from '../store/ar.paths'

type SubmitResult = 'success' | 'error' | 'invalid-campaign' | undefined

interface RegistrationResponse {
  readonly state?: string
}

const HCAPTCHA_SITE_KEY = 'aa943d08-bd74-48ac-a768-82c8c6382cbc'

async function readErrorResult(response: Response): Promise<SubmitResult> {
  try {
    const {state} = (await response.json()) as RegistrationResponse
    return state === 'ERROR_INVALID_CAMPAIGN' ? 'invalid-campaign' : 'error'
  } catch {
    return 'error'
  }
}

export const ArTrialAccountPage = () => {
  const {css, theme} = useFela<Theme>()

  const [email, setEmail] = React.useState('')
  const [firstName, setFirstName] = React.useState('')
  const [lastName, setLastName] = React.useState('')
  const [phone, setPhone] = React.useState('')
  const [company, setCompany] = React.useState('')
  const [campaign, setCampaign] = React.useState('')
  const [submitted, setSubmitted] = React.useState(false)
  const [submitting, setSubmitting] = React.useState(false)
  const [submitResult, setSubmitResult] = React.useState<SubmitResult>(undefined)
  const [captchaToken, setCaptchaToken] = React.useState<string | undefined>(undefined)

  const emailRef = React.useRef<HTMLInputElement>(null)
  const captchaRef = React.useRef<HCaptcha>(null)

  const emailValid = emailRef.current ? emailRef.current.validity.valid : false
  const firstNameValid = firstName.trim().length > 0
  const lastNameValid = lastName.trim().length > 0
  const phoneValid = phone.trim().length > 0
  const companyValid = company.trim().length > 0
  const captchaValid = captchaToken !== undefined && captchaToken.length > 0

  const landingpageParam = new URLSearchParams(window.location.search).get(digitalQueryParams.landingpage)
  const landingpageInvalid = landingpageParam !== null && !isLandingpage(landingpageParam)
  const landingpage: Landingpage =
    landingpageParam !== null && isLandingpage(landingpageParam) ? landingpageParam : DEFAULT_LANDINGPAGE
  const campaignEnabled = supportsCampaign(landingpage)

  const handleSubmit = React.useCallback(() => {
    setSubmitted(true)

    if (!emailValid || !firstNameValid || !lastNameValid || !phoneValid || !companyValid || !captchaValid) {
      return
    }

    setSubmitting(true)
    setSubmitResult(undefined)

    fetch(`/api/v1/auth/start-registration?captcha-response=${encodeURIComponent(captchaToken ?? '')}`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({
        email: email.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        company: company.trim(),
        product: 'ARFBR',
        landingPage: landingpage,
        campaign: campaignEnabled ? campaign.trim() : undefined,
      }),
    })
      .then((response) => (response.ok ? 'success' : readErrorResult(response)))
      .then((result) => {
        if (result !== 'success') {
          setCaptchaToken(undefined)
          captchaRef.current?.resetCaptcha()
        }
        setSubmitResult(result)
      })
      .catch(() => {
        setCaptchaToken(undefined)
        captchaRef.current?.resetCaptcha()
        setSubmitResult('error')
      })
      .finally(() => {
        setSubmitting(false)
      })
  }, [
    email,
    emailValid,
    firstName,
    firstNameValid,
    lastName,
    lastNameValid,
    phone,
    phoneValid,
    company,
    companyValid,
    campaign,
    campaignEnabled,
    captchaToken,
    captchaValid,
    landingpage,
  ])

  const handleCaptchaVerify = React.useCallback((token: string) => {
    setCaptchaToken(token)
  }, [])

  const handleCaptchaExpire = React.useCallback(() => {
    setCaptchaToken(undefined)
  }, [])

  const formStyle: IStyle = {
    width: '100%',
    maxWidth: 500,
  }
  const inputStyle: IStyle = {
    width: '100%',
    boxSizing: 'border-box',
    border: theme.borders.border1,
    padding: theme.spacing.space2,
    fontSize: '100%',
  }
  const errorStyle: IStyle = {
    color: theme.colors.accent,
    fontSize: '85%',
    marginTop: theme.spacing.space1,
  }
  const labelStyle: IStyle = {
    display: 'block',
    marginBottom: theme.spacing.space1,
    fontWeight: 'bold',
  }
  const fieldStyle: IStyle = {
    marginBottom: theme.spacing.space4,
  }
  const consentTextStyle: IStyle = {
    fontSize: '85%',
  }

  const arHeader = <ARHeaderPanel />
  const arFooter = <ReaderFooterPanel />
  const canonical = arPathTrialAccount()

  if (submitResult === 'success') {
    return (
      <ReaderLayout header={arHeader} footer={arFooter} enableScrollTop>
        <DigitalHelmet title="Arbeitsrecht für Betriebsräte - Testzugang" canonical={canonical} />
        <MainContainerPanel>
          <Flex direction="column" fullWidth pt={12} pb={12} maxWidth={800} style={{margin: '0 auto'}}>
            <h1>Neu beim ÖGB-Verlag</h1>
            <Box pt={4} pb={8}>
              <p>
                Du erhältst in Kürze eine E-Mail mit allen Informationen zu deinem Testzugang. Danach kannst du sofort
                auf die Inhalte von <b>„Arbeitsrecht für Betriebsräte“</b> zugreifen.
              </p>
              <p>
                <b>Wir wünschen dir viel Erfolg beim Entdecken und Ausprobieren.</b>
              </p>
            </Box>
          </Flex>
        </MainContainerPanel>
      </ReaderLayout>
    )
  }

  if (landingpageInvalid) {
    return (
      <ReaderLayout header={arHeader} footer={arFooter} enableScrollTop>
        <DigitalHelmet title="Arbeitsrecht für Betriebsräte - Testzugang" canonical={canonical} />
        <MainContainerPanel>
          <Flex direction="column" fullWidth pt={12} pb={12} maxWidth={800} style={{margin: '0 auto'}}>
            <h1>Neu beim ÖGB-Verlag?</h1>
            <Box pt={4} pb={8}>
              <div className={css(errorStyle)}>
                Die angegebene Landingpage „{landingpageParam}" wird nicht unterstützt.
              </div>
            </Box>
          </Flex>
        </MainContainerPanel>
      </ReaderLayout>
    )
  }

  return (
    <ReaderLayout header={arHeader} footer={arFooter} enableScrollTop>
      <DigitalHelmet title="Arbeitsrecht für Betriebsräte - Testzugang" canonical={canonical} />
      <MainContainerPanel>
        <Flex direction="column" fullWidth pt={12} pb={12} maxWidth={800} style={{margin: '0 auto'}}>
          <h1>Neu beim ÖGB-Verlag?</h1>
          <ArTrialAccountIntro landingpage={landingpage} />

          <Box pt={2} pb={2}>
            <b>Jetzt kostenlosen Testzugang erstellen</b>
          </Box>

          <div className={css(formStyle)}>
            <div className={css(fieldStyle)}>
              <label className={css(labelStyle)}>E-Mail</label>
              <input
                ref={emailRef}
                className={css(inputStyle)}
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {submitted && !emailValid && (
                <div className={css(errorStyle)}>Bitte gib eine gültige E-Mail-Adresse ein.</div>
              )}
            </div>

            <div className={css(fieldStyle)}>
              <label className={css(labelStyle)}>Vorname</label>
              <input
                className={css(inputStyle)}
                type="text"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
              {submitted && !firstNameValid && <div className={css(errorStyle)}>Bitte gib deinen Vornamen ein.</div>}
            </div>

            <div className={css(fieldStyle)}>
              <label className={css(labelStyle)}>Nachname</label>
              <input
                className={css(inputStyle)}
                type="text"
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
              {submitted && !lastNameValid && <div className={css(errorStyle)}>Bitte gib deinen Nachnamen ein.</div>}
            </div>

            <div className={css(fieldStyle)}>
              <label className={css(labelStyle)}>Telefon</label>
              <input
                className={css(inputStyle)}
                type="tel"
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              {submitted && !phoneValid && <div className={css(errorStyle)}>Bitte gib deine Telefonnummer ein.</div>}
            </div>

            <div className={css(fieldStyle)}>
              <label className={css(labelStyle)}>Firma</label>
              <input
                className={css(inputStyle)}
                type="text"
                required
                value={company}
                onChange={(e) => setCompany(e.target.value)}
              />
              {submitted && !companyValid && <div className={css(errorStyle)}>Bitte gib deine Firma ein.</div>}
            </div>

            {campaignEnabled && (
              <div className={css(fieldStyle)}>
                <label className={css(labelStyle)}>Rabattcode</label>
                <input
                  className={css(inputStyle)}
                  type="text"
                  value={campaign}
                  onChange={(e) => setCampaign(e.target.value)}
                />
                {submitResult === 'invalid-campaign' && (
                  <div className={css(errorStyle)}>Der Rabattcode ist ungültig oder abgelaufen.</div>
                )}
              </div>
            )}
          </div>

          <div className={css(formStyle)}>
            <div className={css(fieldStyle)}>
              <HCaptcha
                ref={captchaRef}
                sitekey={HCAPTCHA_SITE_KEY}
                onVerify={handleCaptchaVerify}
                onExpire={handleCaptchaExpire}
              />
              {submitted && !captchaValid && (
                <div className={css(errorStyle)}>Bitte bestätige, dass du kein Roboter bist.</div>
              )}
            </div>

            <div className={css(consentTextStyle)}>
              Mit dem Absenden dieses Formulars stimme ich der Einrichtung eines Testzugangs, der Zusendung einer
              Registrierungs-E-Mail sowie der Kontaktaufnahme für Rückfragen zum Testzugang und zum Produkt Arbeitsrecht
              für Betriebsräte zu. Diese Einwilligung kann ich jederzeit per E-Mail an{' '}
              <a href="mailto:datenschutz@oegbverlag.at">datenschutz@oegbverlag.at</a> widerrufen. Nähere Informationen
              zur Verarbeitung meiner personenbezogenen Daten finde ich in der{' '}
              <ArLink
                name="Datenschutzerklärung"
                link="https://www.oegbverlag.at/cms/V01/V01_999_datenschutz/datenschutz"
              />{' '}
              des ÖGB-Verlags.
              <br />
              Ich akzeptiere die{' '}
              <ArLink name="Fair Use Policy" link="https://arbeitsrecht-fuer-betriebsraete.at/fair-use-policy" /> in der
              jeweils gültigen Fassung.
            </div>

            <Box pt={2} pb={4}>
              <Button
                type={Type.CTA}
                text={submitting ? 'Wird gesendet...' : 'Registrieren'}
                linkTo={handleSubmit}
                disabled={submitting}
              />
            </Box>

            {submitResult === 'error' && (
              <Box pb={4}>
                <div className={css(errorStyle)}>
                  Bei der Registrierung ist ein Fehler aufgetreten. Bitte versuche es später erneut.
                </div>
              </Box>
            )}
            {submitResult === 'invalid-campaign' && (
              <Box pb={4}>
                <div className={css(errorStyle)}>Bitte überprüfe deinen Rabattcode.</div>
              </Box>
            )}
          </div>

          <Box pb={8}>
            Bereits registriert? <ArLink name="Hier einloggen" link={digitalLoginPage()} />
          </Box>
        </Flex>
      </MainContainerPanel>
    </ReaderLayout>
  )
}
